import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";

import { Skeleton } from "../ui/Skeleton";
import { CommunityEmptyState } from "../../features/community/CommunityEmptyState";
import { FeedItem } from "../../features/community/FeedItem";
import { useCommunityFeed } from "../../features/community/hooks";

export function DesktopRightRail() {
  const { t } = useTranslation();
  const location = useLocation();
  // The /community route is the canonical full-feed destination; the rail
  // returns null there. The hook short-circuits via enabled=false so we don't
  // run the community_activity_feed RPC or open a subscriber for a feed that
  // won't render (the /community route owns the single community:feed
  // subscription). Leaving /community flips enabled false->true, which re-runs
  // the query — that, not a remount, is what refreshes the rail (the rail stays
  // mounted across navigation). refetchOnMount: "always" only covers the cold
  // first mount of the shell.
  const onCommunity = location.pathname === "/community";
  const { data, isLoading, isError, refreshFailed } = useCommunityFeed(12, {
    enabled: !onCommunity,
    refetchOnMount: "always",
  });

  if (onCommunity) return null;

  return (
    <aside
      aria-label={t("community.feed_title")}
      className="sticky top-0 flex h-screen flex-col"
    >
      <header className="border-b border-line px-4 py-3">
        <h2 className="label-caps text-fg-mute">{t("community.feed_title")}</h2>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {refreshFailed && data && data.length > 0 && (
          <p
            role="status"
            className="mb-2 rounded border border-ember/30 bg-ember/5 px-2 py-1 text-xs text-ember"
          >
            {t("community.feed_stale")}
          </p>
        )}

        {isLoading && (
          <ul className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <li key={i}>
                <Skeleton className="h-16 w-full" />
              </li>
            ))}
          </ul>
        )}

        {isError && <p className="text-sm text-ember">{t("community.feed_failed")}</p>}

        {!isLoading && !isError && (!data || data.length === 0) && <CommunityEmptyState compact />}

        {!isLoading && data && data.length > 0 && (
          <ul className="space-y-2">
            {data.map((event) => (
              <FeedItem key={event.event_id} event={event} />
            ))}
          </ul>
        )}
      </div>

      <footer className="border-t border-line px-4 py-3">
        <Link to="/community" className="text-xs text-fg-mute hover:text-fg">
          {t("community.view_all")}
        </Link>
      </footer>
    </aside>
  );
}
